import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { deleteUser } from '../slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const DeleteUser = ({ open, handleClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Email específico a eliminar
  const targetEmail = 'abalarezoc@hotmail.com';

  const handleDeleteUser = async () => {
    // Verificar que el email confirmado coincide con el email objetivo
    if (confirmEmail !== targetEmail) {
      setError(language === 'es' 
        ? 'El correo electrónico ingresado no coincide' 
        : 'The email entered does not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const resultAction = await dispatch(deleteUser(targetEmail));
      
      if (deleteUser.fulfilled.match(resultAction)) {
        setSuccess(true);
        // Redirigir después de 2 segundos
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        // Extraer detalles del error de la respuesta
        const errorPayload = resultAction.payload || {};
        const errorResponse = errorPayload.response || {};
        const errorData = errorResponse.data || {};
        
        // Registrar detalles del error en la consola para depuración
        console.error('Error details:', {
          message: errorPayload.message,
          status: errorResponse.status,
          statusText: errorResponse.statusText,
          data: errorData
        });
        
        // Mostrar mensaje de error detallado
        let errorMessage = '';
        
        if (errorResponse.status === 401) {
          errorMessage = language === 'es' 
            ? 'No autorizado: Por favor inicie sesión para realizar esta acción' 
            : 'Unauthorized: Please log in to perform this action';
        } else if (errorResponse.status === 403) {
          errorMessage = language === 'es' 
            ? 'Prohibido: No tiene permisos para eliminar este usuario' 
            : 'Forbidden: You don\'t have permission to delete this user';
        } else if (errorResponse.status === 404) {
          errorMessage = language === 'es' 
            ? 'Usuario no encontrado: El usuario con correo ' + targetEmail + ' no existe' 
            : 'User not found: The user with email ' + targetEmail + ' does not exist';
        } else if (errorResponse.status === 500) {
          errorMessage = language === 'es' 
            ? 'Error del servidor: ' + (errorData.message || 'Error interno del servidor') 
            : 'Server error: ' + (errorData.message || 'Internal server error');
        } else {
          // Intentar obtener el mensaje detallado de varias fuentes posibles
          errorMessage = errorData.message || errorPayload.message || errorResponse.statusText || 
            (language === 'es' ? 'Error al eliminar el usuario: ' : 'Error deleting user: ') + 
            (errorResponse.status ? `Código ${errorResponse.status}` : 'Error desconocido');
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Exception in deleteUser handling:', err);
      setError(language === 'es' 
        ? 'Excepción inesperada: ' + (err.message || 'Error desconocido') 
        : 'Unexpected exception: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-user-dialog-title"
    >
      <DialogTitle id="delete-user-dialog-title">
        {language === 'es' ? 'Eliminar Usuario' : 'Delete User'}
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success">
            {language === 'es' 
              ? `Usuario ${targetEmail} eliminado correctamente` 
              : `User ${targetEmail} successfully deleted`}
          </Alert>
        ) : (
          <>
            <DialogContentText>
              {language === 'es' 
                ? `Estás a punto de eliminar el usuario con email ${targetEmail}. Esta acción no se puede deshacer.` 
                : `You are about to delete the user with email ${targetEmail}. This action cannot be undone.`}
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
              {language === 'es' 
                ? 'Para confirmar, ingresa el correo electrónico del usuario:' 
                : 'To confirm, enter the user email:'}
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="confirmEmail"
              label={language === 'es' ? 'Correo electrónico' : 'Email'}
              type="email"
              fullWidth
              variant="outlined"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              disabled={loading}
              error={!!error}
              helperText={error}
            />
          </>
        )}
        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!success && (
          <>
            <Button onClick={handleClose} color="primary" disabled={loading}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleDeleteUser} 
              color="error" 
              disabled={loading || confirmEmail !== targetEmail}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                language === 'es' ? 'Eliminar' : 'Delete'
              )}
            </Button>
          </>
        )}
        {success && (
          <Button onClick={() => navigate('/login')} color="primary">
            {language === 'es' ? 'Ir a inicio de sesión' : 'Go to login'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteUser;
